import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  HeartPulse,
  IdCard,
  Lock,
  Network,
  Phone,
  ShieldCheck,
  Smartphone,
  Stethoscope,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const PAGES = {
  abdm: {
    eyebrow: 'Ayushman Bharat Digital Mission',
    title: "ABDM connects India's digital health ecosystem.",
    intro:
      'ABDM is the Government of India digital health framework from the National Health Authority. It helps patients, doctors, hospitals, labs, and health apps exchange records with consent.',
    icon: Network,
    cta: 'Link ABHA in dashboard',
    official: 'https://abdm.gov.in/',
    points: [
      ['Digital health backbone', 'ABDM creates standards and infrastructure for secure, interoperable health record exchange.'],
      ['Consent-based sharing', 'Patients remain in control of when and where their health records are shared.'],
      ['Works across providers', 'The ecosystem is designed for hospitals, clinics, labs, insurers, and health applications.'],
      ['Useful for Health Buddy', 'ABDM identity matching helps Health Buddy prepare for future consent-based health record exchange.'],
    ],
    stepsTitle: 'How Health Buddy fits with ABDM',
    steps: [
      'Patient creates or already has an ABHA number/address.',
      'Patient logs in to Health Buddy and links ABHA from the dashboard.',
      'ABDM sends OTP to the mobile linked with that ABHA.',
      'After verification, Health Buddy stores only the linked ABHA reference for identity matching.',
    ],
  },
  'abha-card': {
    eyebrow: 'ABHA Card',
    title: 'ABHA is your digital health identity.',
    intro:
      'ABHA stands for Ayushman Bharat Health Account. It can include a 14-digit ABHA number and an easy-to-remember ABHA address such as name@abdm.',
    icon: IdCard,
    cta: 'Link your ABHA',
    official: 'https://abha.abdm.gov.in/abha/v3/',
    points: [
      ['14-digit health number', 'The ABHA number uniquely identifies a person in the ABDM ecosystem.'],
      ['ABHA address', 'An address such as username@abdm can be used with ABDM-enabled health apps.'],
      ['Record continuity', 'ABHA helps link records from different participating providers with patient consent.'],
      ['Not a prescription card', 'ABHA is a digital identity and record-linking layer, not a medical diagnosis or treatment card.'],
    ],
    stepsTitle: 'What you need to link ABHA here',
    steps: [
      'Your ABHA number or ABHA address.',
      'Access to the mobile number registered with your ABHA.',
      'The OTP sent through ABDM.',
      'A Health Buddy account so the linked ABHA stays private to your login.',
    ],
  },
  phr: {
    eyebrow: 'PHR Apps',
    title: 'PHR apps help patients view and manage health records.',
    intro:
      'A Personal Health Record app lets people keep health records in one place and manage consent for sharing records through ABDM-supported flows.',
    icon: Smartphone,
    cta: 'Open dashboard',
    official: 'https://play.google.com/store/apps/details?id=in.ndhm.phr',
    points: [
      ['Personal record view', 'Patients can keep reports, prescriptions, and visit records easier to find.'],
      ['Consent management', 'Records should be shared only after the patient authorizes access.'],
      ['ABHA connection', 'PHR apps commonly use ABHA number or ABHA address for identity and record linking.'],
      ['Health Buddy reports', 'Health Buddy creates intake summaries and PDFs that can support doctor conversations.'],
    ],
    stepsTitle: 'How to use PHR with Health Buddy',
    steps: [
      'Keep your official records in an ABDM-enabled PHR app.',
      'Use Health Buddy when you need symptom intake or a doctor-ready summary.',
      'Download the Health Buddy PDF for visits, agents, and doctors.',
      'Use ABHA linking in Health Buddy as the bridge for future record exchange.',
    ],
  },
};

export default function HealthResources({ page = 'abdm' }) {
  const data = PAGES[page] || PAGES.abdm;
  const Icon = data.icon;

  return (
    <div className="bg-[#FAFAFA]">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-[1fr_0.8fr] gap-10 items-center">
          <div>
            <Badge className="rounded-full bg-[#F4F1EA] text-[#1E3F33] border border-[#E8E1D5] hover:bg-[#F4F1EA] px-4 py-1.5 text-sm font-semibold">
              {data.eyebrow}
            </Badge>
            <h1 className="font-display text-4xl sm:text-6xl text-[#1A1A1A] leading-tight mt-5">
              {data.title}
            </h1>
            <p className="text-lg text-[#4A4A4A] mt-5 leading-relaxed max-w-2xl">
              {data.intro}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/dashboard">
                <Button className="rounded-full h-12 px-6 bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
                  {data.cta} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href={data.official} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full h-12 px-6 border-[#1E3F33] text-[#1E3F33] font-semibold">
                  Official resource
                </Button>
              </a>
            </div>
          </div>

          <div className="rounded-[28px] bg-[#1E3F33] text-white p-8 sm:p-10 hb-shadow-lg">
            <div className="h-16 w-16 rounded-2xl bg-white/12 grid place-items-center">
              <Icon className="h-8 w-8 text-[#E07A5F]" />
            </div>
            <h2 className="font-display text-3xl mt-6">Health Buddy status</h2>
            <p className="text-white/85 mt-3 leading-relaxed">
              The app already supports ABHA linking from the patient dashboard. Production ABDM use requires approved ABDM credentials and registered callback URLs.
            </p>
            <div className="grid gap-3 mt-6 text-sm">
              <div className="rounded-2xl bg-white/10 border border-white/15 p-4 flex gap-3">
                <ShieldCheck className="h-5 w-5 text-[#E07A5F] shrink-0" />
                OTP verification is handled through ABDM.
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/15 p-4 flex gap-3">
                <Lock className="h-5 w-5 text-[#E07A5F] shrink-0" />
                ABHA details are private to the logged-in patient.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.points.map(([title, text]) => (
              <Card key={title} className="p-6 rounded-2xl border border-[#E8E1D5] bg-[#FAFAFA] hb-shadow-soft">
                <ClipboardCheck className="h-6 w-6 text-[#1E3F33]" />
                <h3 className="font-display text-xl text-[#1A1A1A] mt-4">{title}</h3>
                <p className="text-[15px] text-[#4A4A4A] mt-2 leading-relaxed">{text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[0.8fr_1fr] gap-8">
          <div>
            <h2 className="font-display text-4xl text-[#1A1A1A] leading-tight">{data.stepsTitle}</h2>
            <p className="text-[#4A4A4A] mt-4 leading-relaxed">
              These pages explain the ABDM/ABHA connection clearly for patients, agents, and partners before they link anything.
            </p>
          </div>
          <div className="grid gap-4">
            {data.steps.map((step, index) => (
              <div key={step} className="flex items-start gap-4 rounded-2xl bg-white border border-[#E8E1D5] p-5">
                <div className="h-9 w-9 rounded-full bg-[#1E3F33] text-white grid place-items-center font-semibold shrink-0">
                  {index + 1}
                </div>
                <p className="text-[#1A1A1A] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F4F1EA] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-5">
          <Card className="p-6 rounded-2xl bg-white border border-[#E8E1D5]">
            <HeartPulse className="h-6 w-6 text-[#1E3F33]" />
            <h3 className="font-display text-xl mt-4">For patients</h3>
            <p className="text-[15px] text-[#4A4A4A] mt-2">Use ABHA for identity and Health Buddy for symptom intake, report links, and PDF summaries.</p>
          </Card>
          <Card className="p-6 rounded-2xl bg-white border border-[#E8E1D5]">
            <Stethoscope className="h-6 w-6 text-[#1E3F33]" />
            <h3 className="font-display text-xl mt-4">For doctors</h3>
            <p className="text-[15px] text-[#4A4A4A] mt-2">Review structured intake summaries with patient context, urgency, and downloadable PDFs.</p>
          </Card>
          <Card className="p-6 rounded-2xl bg-white border border-[#E8E1D5]">
            <FileText className="h-6 w-6 text-[#1E3F33]" />
            <h3 className="font-display text-xl mt-4">For agents</h3>
            <p className="text-[15px] text-[#4A4A4A] mt-2">Help patients open WhatsApp report links, download PDFs, and understand the ABHA linking flow.</p>
          </Card>
        </div>
      </section>
    </div>
  );
}
